import { ApiError } from "@/lib/api/client";
import { bareAdAccountId } from "@/lib/ads/adAccounts";
import { sameAdAccount, type AdsSyncPin } from "@/lib/ads/adsSyncPin";

export const MANUAL_ROUND_LIMIT = 6;
export const IMPORT_ROUND_LIMIT = 15;
export const RATE_LIMIT_BACKOFF_MS = 15 * 60 * 1000;
export const FOLLOW_POLL_MS = 4000;
export const FOLLOW_LIMIT_MS = 30 * 60 * 1000;
export const FOLLOW_MAX_READ_ERRORS = 5;

export type SyncMode = "manual" | "import";

export interface SyncRoundResult {
  status: string;
  partial?: boolean;
  insights_complete?: boolean;
  ad_account_id?: string | null;
  chunks_done?: number;
  chunks_total?: number;
  requested_at?: string | null;
  [key: string]: unknown;
}

export interface SyncRunResult extends SyncRoundResult {
  passes: number;
  mode: SyncMode;
  complete: boolean;
}

export interface SyncStatusLike {
  connected?: boolean;
  status?: string;
  ad_account_id?: string | null;
  is_running?: boolean;
  is_queued?: boolean;
  last_synced_at?: string | null;
  synced_through?: string | null;
  breakdowns_synced_through?: string | null;
  import_complete?: boolean;
  last_error_type?: string | null;
  last_error_message?: string | null;
  last_started_at?: string | null;
  retry_available_at?: string | null;
}

export interface SyncRunOptions {
  mode: SyncMode;
  syncOnce: (pin: AdsSyncPin) => Promise<SyncRoundResult>;
  readStatus?: (pin: AdsSyncPin) => Promise<SyncStatusLike>;
  onRound?: (pin: AdsSyncPin) => void;
  onQueued?: (pin: AdsSyncPin) => void;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

export function syncAgainAt(retryAvailableAt: string | null | undefined): string | null {
  const at = retryAvailableAt ? new Date(retryAvailableAt) : null;
  if (!at || Number.isNaN(at.getTime())) return null;
  return at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function retryWaitMs(status: SyncStatusLike | null | undefined, now: number = Date.now()): number {
  const at = status?.retry_available_at ? Date.parse(status.retry_available_at) : NaN;
  return Number.isFinite(at) && at > now ? at - now : 0;
}

export const errorCodeOf = (error: unknown): string | undefined => {
  const e = error as { code?: unknown; meta?: { code?: unknown }; statusCode?: unknown; status?: unknown; message?: unknown; error?: unknown } | null;
  const code = e?.code ?? e?.meta?.code;
  if (typeof code === "string" && code) return code;
  if (e?.statusCode === 504 || e?.status === 504) return "TIMEOUT";
  const text = typeof e?.message === "string" ? e.message : typeof e?.error === "string" ? e.error : "";
  return /endpoint request timed out/i.test(text) ? "TIMEOUT" : undefined;
};

export const pinKey = (pin: AdsSyncPin): string => `${pin.workspaceId}|${bareAdAccountId(pin.adAccountId)}`;

const inFlight = new Map<string, Promise<SyncRunResult>>();
const listeners = new Set<() => void>();
let importChain: Promise<unknown> = Promise.resolve();
let version = 0;

const notify = () => {
  version += 1;
  for (const listener of Array.from(listeners)) listener();
};

export function subscribeSyncRuns(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function syncRunsVersion(): number {
  return version;
}

export function isSyncRunning(pin: AdsSyncPin | null | undefined): boolean {
  return !!pin && inFlight.has(pinKey(pin));
}

export function forgetSyncRunsOutside(workspaceId: string | null | undefined): void {
  let changed = false;
  for (const key of Array.from(inFlight.keys())) {
    if (!workspaceId || !key.startsWith(`${workspaceId}|`)) {
      inFlight.delete(key);
      changed = true;
    }
  }
  if (changed) notify();
}

export function resetSyncRunsForTests(): void {
  inFlight.clear();
  listeners.clear();
  importChain = Promise.resolve();
  version = 0;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const accountMismatch = () =>
  new ApiError({
    error: "The sync answered for a different ad account, so it was stopped.",
    message: "The sync answered for a different ad account, so it was stopped.",
    code: "ACCOUNT_MISMATCH",
  });

function failedSyncError(status: SyncStatusLike): ApiError {
  const code = status.last_error_type || "UNKNOWN";
  const message =
    status.last_error_message ||
    (code === "AUTH" ? "Your Facebook sign-in has expired. Please reconnect Facebook to sync." : "The sync did not finish.");
  return new ApiError({ error: message, message, code });
}

async function follow(
  pin: AdsSyncPin,
  started: SyncRoundResult,
  { readStatus, onRound, sleep = defaultSleep, now = Date.now }: SyncRunOptions,
): Promise<SyncRoundResult> {
  const readOnce = readStatus as (pin: AdsSyncPin) => Promise<SyncStatusLike>;
  const begin = now();
  let readErrors = 0;
  let lastProgress: string | null = null;
  let failedOnce = false;
  while (now() - begin < FOLLOW_LIMIT_MS) {
    await sleep(FOLLOW_POLL_MS);
    let status: SyncStatusLike;
    try {
      status = await readOnce(pin);
      readErrors = 0;
    } catch (err) {
      readErrors += 1;
      if (readErrors >= FOLLOW_MAX_READ_ERRORS) throw err;
      continue;
    }
    if (status?.ad_account_id && !sameAdAccount(status.ad_account_id, pin.adAccountId)) throw accountMismatch();
    if (status?.is_running || status?.is_queued) {
      failedOnce = false;
      const progress = [status.last_synced_at, status.synced_through, status.breakdowns_synced_through].join("|");
      if (lastProgress !== null && progress !== lastProgress) onRound?.(pin);
      lastProgress = progress;
      continue;
    }
    if (status?.status === "failed" && !failedOnce) {
      failedOnce = true;
      continue;
    }
    onRound?.(pin);
    if (status?.status === "failed") throw failedSyncError(status);
    return {
      status: "completed",
      partial: false,
      insights_complete: status?.import_complete !== false,
      ad_account_id: status?.ad_account_id ?? started.ad_account_id ?? null,
      followed: true,
    };
  }
  return { ...started, status: "running", partial: true, insights_complete: false };
}

async function loop(pin: AdsSyncPin, options: SyncRunOptions, onHandedOff: () => void = () => {}): Promise<SyncRunResult> {
  const { mode, syncOnce, onRound } = options;
  const limit = mode === "import" ? IMPORT_ROUND_LIMIT : MANUAL_ROUND_LIMIT;
  let passes = 0;
  let last: SyncRoundResult | null = null;
  let lastError: unknown = null;

  while (passes < limit) {
    let res: SyncRoundResult;
    try {
      res = await syncOnce(pin);
    } catch (err) {
      passes += 1;
      onRound?.(pin);
      lastError = err;
      if (errorCodeOf(err) === "TIMEOUT" && passes < limit) continue;
      throw err;
    }
    passes += 1;
    onRound?.(pin);
    if (res?.ad_account_id && !sameAdAccount(res.ad_account_id, pin.adAccountId)) throw accountMismatch();
    last = res;
    if ((res.status === "queued" || res.status === "running") && options.readStatus) {
      onHandedOff();
      if (res.status === "queued") options.onQueued?.(pin);
      last = await follow(pin, res, options);
      break;
    }
    if (res.status === "running") break;
    const more = mode === "import" ? Boolean(res.partial) || res.insights_complete === false : Boolean(res.partial);
    if (!more) break;
  }

  if (!last) throw lastError;
  const complete = mode === "import" ? !last.partial && last.insights_complete !== false : !last.partial;
  return { ...last, passes, mode, complete };
}

export function runPinnedSync(pin: AdsSyncPin, options: SyncRunOptions): Promise<SyncRunResult> {
  const key = pinKey(pin);
  const existing = inFlight.get(key);
  if (existing) return existing;
  let releaseSlot: () => void = () => {};
  let start: Promise<SyncRunResult>;
  if (options.mode === "import") {
    const slot = new Promise<void>((resolve) => {
      releaseSlot = resolve;
    });
    const previous = importChain;
    importChain = previous.then(() => slot);
    start = previous.then(() => loop(pin, options, releaseSlot));
  } else {
    start = loop(pin, options);
  }
  const tracked: Promise<SyncRunResult> = start.finally(() => {
    releaseSlot();
    if (inFlight.get(key) === tracked) {
      inFlight.delete(key);
      notify();
    }
  });
  inFlight.set(key, tracked);
  notify();
  return tracked;
}

export function shouldAutoImport(status: SyncStatusLike | null | undefined, now: number = Date.now()): boolean {
  if (!status?.connected || status.is_running || status.is_queued) return false;
  if (retryWaitMs(status, now) > 0) return false;
  if (status.import_complete === true) return false;
  if (status.import_complete === undefined && status.status !== "never_synced") return false;
  if (status.status === "failed" && status.last_error_type === "AUTH") return false;
  if (status.status === "failed" && status.last_error_type === "RATE_LIMIT") {
    const started = status.last_started_at ? Date.parse(status.last_started_at) : NaN;
    if (!Number.isFinite(started) || now - started < RATE_LIMIT_BACKOFF_MS) return false;
  }
  return true;
}

export const AUTO_IMPORT_STORAGE_PREFIX = "wn.ads.autoImport.";

export function claimAutoImportAttempt(pin: AdsSyncPin, storage: Pick<Storage, "getItem" | "setItem"> | null = null): boolean {
  try {
    const store = storage ?? window.sessionStorage;
    const key = `${AUTO_IMPORT_STORAGE_PREFIX}${pinKey(pin)}`;
    if (store.getItem(key)) return false;
    store.setItem(key, "1");
    return store.getItem(key) === "1";
  } catch {
    return false;
  }
}
